<template>
  <v-row class="pb-10" v-scroll="onScroll">
    <!-- header -->
    <mini-header title="Proveedores" :breadcrumbs="breadcrumbs" :dense="true" :hiddenTab="true" :scroll="scroll">
      <template v-slot:main>
        <v-text-field
          class="mx-2 input-custom-height"
          v-model.trim="search"
          :placeholder="`Introduce el ${$t('generals.RUT')} o razón social`"
          outlined
          single-line
          dense
          maxlength="60"
          hide-details
          style="min-width: 300px; max-width: 300px;"
          @keyup.enter="searchSupplier"
        >
          <template v-slot:append v-if="search.length > 0">
            <v-btn class="mr-n2" @click.stop="resetSearch" text x-small>
              <v-icon class="mt-1" color="grey-900">mdi-close</v-icon>
            </v-btn>
          </template>
        </v-text-field>
        <v-query-builder class="mr-2" :query-map="filters" model="suppliers" :active.sync="activeFilter" />
        <v-btn color="grey-900" @click="dialogSupplier=true"><v-icon left>mdi-human-dolly</v-icon>Crear proveedor</v-btn>
      </template>
    </mini-header>
    <!-- end header -->

    <v-col cols="12" class="px-0 mt-20">
      <skeleton-list v-if="loading" />
      <template v-else>
        <SupplierList
          :suppliers-list="list"
          :reset="reset"
          :reset-scroll="resetScroll"
          :use-infinite-scroll="true"
          :show-category-column="$store?.state?.auth?.account?.options?.includes('rec_process_rules')"
          :has-more-data="!!nextToken"
          empty-title="No existen proveedores para mostrar"
          @delete="handleTableAddToGroup($event, 'delete')"
          @add="handleTableAddToGroup($event, 'add')"
          @change="handleTableAddToGroup($event, 'change')"
          @load-more="loadMore"
        >
          <template v-slot:empty-actions>
            <template v-if="!$route.query.ordering && hiddenBtnEmpty">
              <v-btn @click="activeFilter = true" color="grey-900" :ripple="false" :width="153"><v-icon class="mr-2">mdi-filter-plus</v-icon>Filtrar</v-btn>
              <v-btn class="ml-2" @click="latestRecords()" outlined :ripple="false" width="153"><v-icon class="mr-2">mdi-text-search-variant</v-icon>Últimos registros</v-btn>
            </template>
          </template>
        </SupplierList>
      </template>
    </v-col>

    <!-- dialog add/change category -->
    <SupplierCategoryModal
      v-if="$store?.state?.auth?.account?.options?.includes('rec_process_rules')"
      v-model="dialogAddGroup"
      :action-type="actionType"
      :supplier="current"
      :categories-list="segmentsList"
      :loading="createGroupLoading"
      @confirm="handleCategoryConfirm"
    />
    <!-- end dialog add/change category -->

    <!-- dialog confirm delete -->
    <SupplierDeleteCategoryModal
      v-if="$store?.state?.auth?.account?.options?.includes('rec_process_rules')"
      v-model="dialogDelete"
      :supplier="current"
      @confirm="handleDeleteConfirm"
    />
    <!-- end dialog confirm delete -->

    <!-- dialog export -->
    <v-dialog v-model="dialogExport" width="700" persistent no-click-animation overlay-color="grey-500">
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar class="transparent" flat height="46">
            <v-toolbar-title class="px-5 subtitle-2 grey-500--text font-weight-semibold">Descarga de proveedores</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-row align="center" justify="center" class="mr-3">
                <v-btn @click="dialogExport=false" icon small retain-focus-on-click><v-icon color="grey-500" size="20">mdi-close</v-icon></v-btn>
              </v-row>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text class="px-5 py-2">
          <v-list class="pa-0 transparent">
            <v-list-item class="mt-2">
              <v-list-item-content class="pt-0">
                <v-list-item-title class="grey-500--text body-1 font-weight-bold">Selecciona el formato de los archivos:</v-list-item-title>
                <v-list-item-subtitle class="pt-2">
                  <v-row class="ml-n1" no-gutters>
                    <v-col cols="6" class="pa-0" v-for="file in fileFormat" :key="file">
                      <v-checkbox class="ma-0" @click="getFileFormat(file)" :input-value="types.includes(file) || false" column :label="file" :disabled="['XLSX', 'CSV'].includes(file) && count >= 1000000" dense hide-details />
                    </v-col>
                  </v-row>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions class="py-2 px-5">
          <v-spacer/>
          <v-btn outlined @click="dialogExport=false" :ripple="false">Cancelar</v-btn>
          <v-btn color="grey-900" @click="createExport" :ripple="false" :disabled="!types.length">Descargar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog export -->

    <!-- dialog new supplier -->
    <v-dialog v-model="dialogSupplier" width="700" persistent no-click-animation scrollable overlay-color="grey-500">
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat height="46" class="transparent">
            <v-toolbar-title class="px-5 subtitle-2 grey-500--text font-weight-semibold">Crear proveedor</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-row align="center" justify="center" class="mr-3">
                <v-btn class="ml-3" @click="dialogSupplier=false, clearInput()" icon small retain-focus-on-click><v-icon color="grey-500" size="20">mdi-close</v-icon></v-btn>
              </v-row>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text class="py-4 px-0 overflow-y-auto" style="max-height: 710px;" min-height="700" scrollable>
          <v-col class="font-weight-semibold d-flex align-center subtitle-2 px-5 text-500--text">
            <v-icon color="grey-500" size="18" left>mdi-human-dolly</v-icon>
            <span class="subtitle-2 grey-500--text font-weight-semibold">Información del proveedor</span>
          </v-col>
          <v-row class="px-5" no-gutters>
            <v-col cols="3" class="pa-0">
              <span class="grey-500--text body-1">{{$t('generals.RUT')}}</span>
              <v-text-field v-model.trim="$v.newSupplier.tax_id.$model" :error="$v.newSupplier.tax_id.$error" v-dni="newSupplier.country" required outlined single-line dense hide-details />
            </v-col>
            <v-col class="pa-0 ml-3">
              <span class="grey-500--text body-1">Nombre de la empresa</span>
              <v-text-field v-model.trim="$v.newSupplier.name.$model" :error="$v.newSupplier.name.$error" outlined required single-line dense maxlength="100" hide-details />
            </v-col>
          </v-row>
          <v-col class="px-5">
            <span class="grey-500--text body-1">Actividad económica</span>
            <v-text-field v-model.trim="newSupplier.tax_settings.cl_activity_description" outlined single-line dense maxlength="64" hide-details />
          </v-col>
          <v-row class="px-5 pb-0" no-gutters>
            <v-col cols="8" class="pa-0">
              <span class="grey-500--text body-1">Dirección</span>
              <v-text-field v-model.trim="newSupplier.address" outlined single-line dense maxlength="64" hide-details />
            </v-col>
            <v-col class="pa-0 ml-3">
              <span class="grey-500--text body-1">{{$t('generals.Comuna')}}</span>
              <v-select v-model="newSupplier.state" :label="$t('generals.Selecciona una comuna')" :items="$store.getters['base/currentStates']" item-value="name" item-text="name" single-line hide-details dense height="30" outlined :no-data-text="`No existen ${$t('generals.Comuna')} para el pais.`">
                <template v-slot:item="data">
                  <v-list-item-title class="font-weight-regular">{{data.item.name}}</v-list-item-title>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <template v-if="$store?.state?.auth?.account?.options?.includes('rec_process_rules')">
            <v-divider class="my-4" />
            <div class="px-5 pt-1">
              <v-toolbar-title class="px-0 mb-3 d-flex align-center">
                <v-icon color="grey-500" size="18" left>mdi-account-multiple</v-icon>
                <span class="subtitle-2 grey-500--text font-weight-semibold">Selecciona la categoría a la que quieres asignar al proveedor</span>
                <v-chip class="ml-2 px-1 font-weight-regular" x-small color="grey-background">Opcional</v-chip>
              </v-toolbar-title>

              <div class="pa-0">
                <span class="grey-500--text body-1">Categorías</span>
                <v-col class="pa-0">
                  <v-select
                    v-model="newSupplier.segment_id"
                    hide-details
                    single-line
                    color="green-500"
                    dense
                    outlined
                    :items="filteredSegmentsListForCreate"
                    :chips="false"
                    item-text="name"
                    item-value="id"
                    item-disabled="disabled"
                    no-data-text="No existen categorías para mostrar"
                    :menu-props="{ bottom: true, offsetY: true, maxHeight: 200 }"
                  />
                </v-col>
              </div>
            </div>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-2">
          <v-spacer/>
          <v-btn outlined @click="dialogSupplier=false, clearInput()">Cancelar</v-btn>
          <v-btn color="grey-900" :loading="$store.state.suppliers.createLoader" @click="postSupplier">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog new supplier -->
  </v-row>
</template>
<script>
import { saveAs } from 'file-saver'
import { base64ToBlob } from '@/utils/download'
import { mapState } from 'vuex'
import PermissionDenied from '@/modules/http/views/PermissionDenied'
import MiniHeader from '@/components/commons/MiniHeader'
import { required, maxLength } from 'vuelidate/lib/validators'
import { rutValidator } from 'vue-dni'
import VQueryBuilder from '@/components/VQueryBuilder/VQueryBuilder'
import SkeletonList from '@/components/commons/skeletonLoader/SkeletonList'
import VTableHeaders from '@/components/VTable/VTableHeaders'
import VEmptyState from '@/components/commons/VEmptyState'
import SupplierList from '@/modules/supplier/components/SupplierList'
import SupplierCategoryModal from '@/modules/supplier/components/SupplierCategoryModal'
import SupplierDeleteCategoryModal from '@/modules/supplier/components/SupplierDeleteCategoryModal'
import ListViewMixin from '@/mixins/ListViewMixin'
import GenericViewMixin from '@/mixins/GenericViewMixin'

export default {
  components: {
    VQueryBuilder,
    VTableHeaders,
    VEmptyState,
    SupplierList,
    SupplierCategoryModal,
    SupplierDeleteCategoryModal,
    SkeletonList,
    MiniHeader,
    PermissionDenied
  },
  mixins: [
    GenericViewMixin,
    ListViewMixin
  ],
  data: () => ({
    defaultSegmentId: 'se_AAAAAAAAAAAAAAAA',
    search: '',
    reset: 0,
    resetScroll: 0,
    nextToken: null,
    moreLoading: false,
    isLoadMore: true,
    list: [],
    contacts: [{ name: '', email: '', phone: '' }],
    types: [],
    dialogExport: false,
    dialogDelete: false,
    auxSelectedList: [],
    group: '',
    createGroupLoading: false,
    dialogAddGroup: false,
    actionType: 'add',
    oldSelected: null,
    isClickCard: false,
    isClickCheckbox: false,
    resetCheckbox: 0,
    actionSelected: '',
    selectedList: [],
    selected: [],
    activeFilter: false,
    scroll: 0,
    confirm: '',
    confirmChange: '',
    isAccept: false,
    current: {},
    newSupplier: {
      name: '',
      tax_id: '',
      address: '',
      segment_id: null,
      tax_settings: {
        cl_activity_description: ''
      },
      contacts: [],
      country: 'CL',
      state: ''
    },
    filters: [
      {
        type: 'text',
        id: 'name',
        label: 'Cliente ',
        value: null,
        category: 'a',
        title: 'Información del proveedor',
        operators: ['contiene'],
        placeholder: 'Escribe el nombre',
        countries: ['PE', 'CL']
      },
      {
        type: 'text',
        id: 'tax_id',
        label: 'RUT',
        value: null,
        category: 'a',
        title: 'Información del proveedor',
        operators: [],
        placeholder: 'Escribe el RUT',
        countries: ['PE', 'CL']
      },
      {
        type: 'text',
        id: 'business_activity',
        label: 'Actividad económica',
        value: null,
        category: 'a',
        title: 'Información del proveedor',
        operators: ['contiene'],
        placeholder: 'Escribe la actividad económica',
        countries: ['PE', 'CL']
      },
      {
        type: 'date',
        id: 'created',
        label: 'Fecha de creación',
        value: null,
        value2: null,
        category: 'b',
        title: 'Fechas',
        operator: 'rango',
        operators: ['es igual', 'es menor a', 'es mayor a', 'rango'],
        countries: ['PE', 'CL']
      }
    ],
    headers: [
      { text: 'Proveedor', value: 'name', class: 'font-weight-bold', sortable: true },
      { text: 'Actividad económica', value: 'tax_settings', sortable: true },
      { text: 'Categoría', value: 'group_details', sortable: true },
      { text: 'Fecha de creación', value: 'created', align: 'end', sortable: true },
      { text: '', value: 'actions', align: 'end'}
    ],
    dialogSupplier: false,
    loading: false,
    breadcrumbs: {
      main: 'Recepción',
      children: [
        {
          text: 'Proveedores'
        }
      ]
    },
    fileFormat: ['XLSX', 'CSV']
  }),
  computed: {
    ...mapState({
      countriesList: state => state.base.countriesList,
      segmentsList: state => state.suppliers.segmentsList,
      count: state => state.suppliers.suppliersCount
    }),
    country () {
      return this.newSupplier.country ? this.countriesList.find(country => country.id === this.newSupplier.country) : null
    },
    hiddenBtnEmpty () {
      if (!Object.keys(this.$route.query ?? {}).length) return true
      else if (!this.list.length) return false
      return false
    },
    heightTable () {
      if (this.$vuetify.breakpoint.height >= 800 && this.list.length < 17) return 'auto'
      return 'calc(100vh - 275px)'
    },
    isDefaultSegment () {
      return (id) => id === this.defaultSegmentId
    },
    filteredSegmentsList () {
      return this.segmentsList.filter(segment =>
        segment.id !== this.defaultSegmentId &&
        segment.id !== this.current?.segment_id
      )
    },
    filteredSegmentsListForCreate () {
      return this.segmentsList.map(segment => ({
        ...segment,
        name: segment.id === this.defaultSegmentId ? 'Sin categoría' : segment.name,
        disabled: segment.id === this.defaultSegmentId
      }))
    }
  },
  watch: {
    '$route.query': {
      handler () {
        this.nextToken = null
        this.getList()
      },
      deep: true
    },
    'newSupplier.country' () {
      this.newSupplier.state = ''
      this.newSupplier.tax_id = ''
    },
    dialogSupplier (val) {
      if (val) {
        this.$store.dispatch('suppliers/LIST', {
          resource: 'segments',
          query: {
            page_size: 9999
          }
        })
      }
    }
  },
  async created () {
    // Limpiar datos anteriores al cambiar de organización
    this.$store.commit('suppliers/RESET_LIST')
    this.loading = true

    if (this.$route.query.group) {
      this.breadcrumbs.children.push({
        text1: this.$route.query.key,
        route1: { name: 'SuppliersCategoriesRetrieveUpdate', params: { id: this.$route.query.group } }
      })
    }
    // Cargar categorías primero
    await this.$store.dispatch('suppliers/LIST', {
      resource: 'segments',
      query: { page_size: 9999 }
    })
    .then((resp) => {
      const index = this.filters.findIndex(({ id }) => id === 'group')
      if (index !== -1) {
        this.filters[index].choices = resp.data.results.map(({ name, id }) => {
          return {
            label: name,
            value: id
          }
        })
      }
    })
    .catch(() => {})
    // Cargar la lista de proveedores
    this.getList()
  },
  methods: {
    searchSupplier () {
      if (!this.search.length) return
      this.reset += 1
      this.loading = true
      this.isLoadMore = false
      this.$store.dispatch('suppliers/LIST', {
        resource: 'suppliers',
        query: { q: this.search.replace(/\./g, '') }
      })
      .then((response) => {
        this.list = this.mapSuppliersWithCategories(response.data)
      })
      .finally(() => {
        this.loading = false
      })
    },
    resetSearch () {
      this.search = ''
      this.isLoadMore = true
      this.nextToken = null
      this.getList()
    },
    clearInput () {
      this.$v.newSupplier.$reset()
      this.newSupplier = {
        name: '',
        tax_id: '',
        tax_settings: {
          cl_activity_description: ''
        },
        country: 'CL',
        state: '',
        address: '',
        segment_id: null
      }
      this.contacts = [{ name: '', email: '', phone: '' }]
    },
    addRowContacts () {
      this.contacts.push({
        name: '',
        email: '',
        phone: ''
      })
    },
    getFileFormat (val = null) {
      if (!this.types?.find((s) => s === val)) this.types.push(val)
      else {
        this.types.splice(this.types.indexOf(val), 1)
        this.types = [...this.types]
      }
    },
    async createExport () {
      let currentQuery = this.deleteQueries()
      Promise.all(this.types.map(async (item) => {
        try {
          const resp = await this.$store.dispatch('accounts/LIST', {
            resource: 'suppliers/export',
            query: {
              extension: item,
              ...(this.auxSelectedList.length && !Object.keys(currentQuery).length && { id__in: this.auxSelectedList.toString() })
            },
            loader: false,
            responseType: 'arraybuffer'
          })
          const content = base64ToBlob(resp.data)
          saveAs(new Blob([content], { type: `application/${item.toLowerCase()}` }), `Listado de proveedores.${item.toLowerCase()}`)
        } catch (e) {
          this.$dialog.message.error('Ha ocurrido un error en la descarga')
        }
      }))
      this.types = []
      this.dialogExport = false
    },
    deleteQueries () {
      let currentQuery = JSON.parse(JSON.stringify(this.$route.query))
      if (currentQuery.ordering) delete currentQuery.ordering
      if (currentQuery.name) delete currentQuery.name
      return currentQuery
    },
    handleTableAddToGroup (item = {}, action = 'add') {
      this.current = item
      this.handleGroup(action)
    },
    async handleGroup (action = '') {
      if (action === 'add' || action === 'change') {
        this.actionType = action
        await this.$store.dispatch('suppliers/LIST', {
          resource: 'segments',
          query: {
            page_size: 9999
          }
        })
        this.dialogAddGroup = true
      } else if (action === 'delete') {
        this.dialogDelete = true
      }
    },
    async handleCategoryConfirm ({ supplier, newCategoryId, oldCategoryId, actionType }) {
      this.createGroupLoading = true
      try {
        if (actionType === 'change') {
          await this.$store.dispatch('suppliers/UPDATE', {
            resource: `suppliers/${supplier.id}/replace-segment`,
            payload: {
              old_segment_id: oldCategoryId,
              new_segment_id: newCategoryId
            }
          })
        } else {
          await this.$store.dispatch('suppliers/UPDATE', {
            resource: `suppliers/${supplier.id}/replace-segment`,
            payload: {
              old_segment_id: oldCategoryId || this.defaultSegmentId,
              new_segment_id: newCategoryId
            }
          })
        }
        this.$dialog.message.info(actionType === 'add' ? 'El proveedor ha sido asignado correctamente a la categoría.' : 'El proveedor ha sido cambiado de categoría correctamente.')
        this.getList()
      } catch (error) {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      } finally {
        this.dialogAddGroup = false
        this.createGroupLoading = false
      }
    },
    handleDeleteConfirm ({ supplier, categoryId }) {
      this.$store.dispatch('suppliers/UPDATE', {
        resource: `suppliers/${supplier.id}/remove-segment`,
        payload: {
          segment_id: categoryId
        }
      })
      .then(() => {
        this.$dialog.message.info('El proveedor ha sido eliminado de la categoría con éxito')
        this.getList()
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
      .finally(() => {
        this.dialogDelete = false
      })
    },
    toggleAll () {
      this.$store.commit('documents/SET_TOOGLE_SENDTYPE', '')
      this.selected = !this.isSelected ? [] : this.list
    },
    onScroll (e) {
      this.scroll = e.target?.scrollingElement?.scrollTop ?? 0
    },
    async getList () {
      this.loading = true
      const currentQuery = JSON.parse(JSON.stringify(this.$route.query))
      if (currentQuery.key) delete currentQuery.key

      // Cargar segmentos primero
      await this.$store.dispatch('suppliers/LIST', {
        resource: 'segments',
        query: { page_size: 9999 }
      })

      // Cargar proveedores
      this.$store.dispatch('suppliers/LIST', {
        resource: 'suppliers',
        query: currentQuery
      })
      .then((response) => {
        this.setNextToken(response.headers['x-ax-next-token'])
        this.list = this.mapSuppliersWithCategories(response.data)
      })
      .finally(() => {
        this.loading = false
      })
    },
    loadMore ({ entries, observer, isIntersecting }) {
      if (isIntersecting && this.isLoadMore && !this.moreLoading) {
        this.moreLoading = true
        const currentQuery = JSON.parse(JSON.stringify(this.$route.query))
        if (currentQuery.key) delete currentQuery.key

        this.$store.dispatch('suppliers/LIST', {
          resource: 'suppliers',
          query: currentQuery,
          headers: {
            'x-ax-next-token': this.nextToken
          }
        })
        .then((response) => {
          this.setNextToken(response.headers['x-ax-next-token'])
          this.resetScroll += 1
          const newSuppliers = this.mapSuppliersWithCategories(response.data)
          this.list = [...this.list, ...newSuppliers]
        })
        .finally(() => {
          this.moreLoading = false
        })
      }
    },
    setNextToken (nextToken) {
      this.nextToken = nextToken ?? null
      this.isLoadMore = this.nextToken?.length > 0
    },
    mapSuppliersWithCategories (suppliers) {
      if (!suppliers?.length) return []
      return suppliers.map(supplier => {
        const isDefaultSegment = !supplier.segment_id || supplier.segment_id === this.defaultSegmentId
        const category = this.segmentsList.find(segment => segment.id === supplier.segment_id)
        return {
          ...supplier,
          segment_id: supplier.segment_id || this.defaultSegmentId,
          category_details: isDefaultSegment
            ? { id: this.defaultSegmentId, name: 'Sin categoría asociada' }
            : (category || null)
        }
      })
    },
    async postSupplier () {
      this.$v.newSupplier.$touch()
      if (this.$v.newSupplier.$invalid) {
        return false
      }
      try {
        const { segment_id: segmentId, ...supplierData } = this.newSupplier

        if (supplierData.tax_id) {
          supplierData.tax_id = supplierData.tax_id.replace(/\./g, '')
        }

        const response = await this.$store.dispatch('suppliers/CREATE', {
          resource: 'suppliers',
          payload: supplierData
        })

        if (segmentId) {
          await this.$store.dispatch('suppliers/UPDATE', {
            resource: `suppliers/${response.data.id}/replace-segment`,
            payload: {
              old_segment_id: this.defaultSegmentId,
              new_segment_id: segmentId
            }
          })
        }

        this.$router.push({ name: 'SupplierRetrieveUpdate', params: { id: response.data.id } })
      } catch (error) {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      } finally {
        this.dialogSupplier = false
      }
    },
    latestRecords () {
      this.$router.push({ query: { ref: 'initial' } })
      this.getList()
    }
  },
  validations: {
    newSupplier: {
      name: { required, maxLength: maxLength(100) },
      tax_id: {
        required,
        format: function (val) {
          return rutValidator(val)
        },
        maxLength: maxLength(16)
      }
    }
  }
}
</script>
<style scoped>
.input-custom-height.v-text-field--outlined ::v-deep .v-input__control > .v-input__slot {
  min-height: 36px !important;
  max-height: 36px !important;
}
.inf-alert-yellow.v-alert--outlined {
  border-color: var(--light-yellow-700) !important;
  background-color: var(--light-yellow-100) !important;
}
</style>
